import { tWithKeyAttr as t } from "../i18nHelper";
import * as Yup from "yup";
import { israeliMobileRegExp } from "./constants";
import { isValidPhone } from "./utils";
import {phoneRegExp} from "./constants";

const imageFormats = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];

export const signUpValidationSchemaFull = () => Yup.object().shape({
  firstName: Yup.string()
    .required(t('FIRST_NAME_MIN_TWO_CHARS'))
    .min(2, t('FIRST_NAME_MIN_TWO_CHARS')),
  lastName: Yup.string()
    .required(t('LAST_NAME_MIN_TWO_CHARS'))
    .min(2, t('LAST_NAME_MIN_TWO_CHARS')),
  passport: Yup.string()
    .required(t('IDENTITY_CARD_LENGTH'))
    .min(9, t('IDENTITY_CARD_LENGTH'))
    .max(9, t('IDENTITY_CARD_LENGTH')),
  email: Yup.string().required(t('INVALID_EMAIL')).email(t('INVALID_EMAIL')),
  phone: Yup.string()
    .required(t('PLEASE_ENTER_MOBILE_NUMBER'))
    .test('phone-format', function(value) {
      if (!value) return true;
      const isValid = phoneRegExp.test(value);
      return isValid || this.createError({ message: t('INVALID_PHONE_NUMBER') });
    }),
  birth: Yup.date()
    .required(t('BIRTH_DATE_INVALID'))
    .min(new Date(1920, 0, 1), t('BIRTH_DATE_INVALID'))
    .max(new Date(), t('BIRTH_DATE_INVALID')),
  genderId: Yup.number().required().min(1),
  country: Yup.string().required(),
  password: Yup.string()
    .required(t('PASSWORD_MUST_BE_6_CHARS'))
    .min(6, t('PASSWORD_MUST_BE_6_CHARS')),
});

export const signUpValidationSchema1 = () => Yup.object().shape({
  firstName: Yup.string()
    .required(t('PLEASE_COMPLETE_REQUIRED_FIELDS'))
    .min(2, t('FIRST_NAME_MIN_TWO_CHARS')),
  lastName: Yup.string()
    .required(t('PLEASE_COMPLETE_REQUIRED_FIELDS'))
    .min(2, t('LAST_NAME_MIN_TWO_CHARS')),
  passport: Yup.string()
    .required(t('PLEASE_COMPLETE_REQUIRED_FIELDS'))
    .test('passport-format', function(value) {
      if (!value) return true; // Let required handle empty values
      const isValid = /^\d{9}$/.test(value);
      return isValid || this.createError({ message: t('IDENTITY_CARD_DIGITS') });
    }),
  email: Yup.string()
    .required(t('PLEASE_COMPLETE_REQUIRED_FIELDS'))
    .email(t('INVALID_EMAIL')),
});

export const signUpValidationSchema2 = () => Yup.object().shape({
  phone: Yup.string()
    .required(t('PLEASE_COMPLETE_REQUIRED_FIELDS'))
    .test('phone-format', function(value) {
      if (!value) return true;
      const isValid = phoneRegExp.test(value);
      return isValid || this.createError({ message: t('INVALID_PHONE_NUMBER') });
    }),
  birth: Yup.date()
    .required(t('BIRTH_DATE_INVALID'))
    .typeError(t('BIRTH_DATE_INVALID'))
    .test("is-valid-date", function(value) {
      const minDate = new Date(1920, 0, 1);
      const maxDate = new Date();
      if (!value) return false; // Required validation
      const date = new Date(value);
      const isValid = date >= minDate && date <= maxDate;
      return isValid || this.createError({ message: t('BIRTH_DATE_INVALID') });
    }),
  genderId: Yup.number().required().min(1),
  country: Yup.string().required(),
});

export const updateUserValidationSchema = () => Yup.object().shape({
  firstName: Yup.string()
    .required(t('PLEASE_ENTER_FIRST_NAME'))
    .min(2, t('FIRST_NAME_MIN_TWO_CHARS')),
  lastName: Yup.string()
    .required(t('PLEASE_ENTER_LAST_NAME'))
    .min(2, t('LAST_NAME_MIN_TWO_CHARS')),
  email: Yup.string()
    .required(t('PLEASE_ENTER_EMAIL'))
    .email(t('INVALID_EMAIL')),
  phone: Yup.string()
    .required(t('PLEASE_ENTER_MOBILE_NUMBER'))
    .test('phone-format', function(value) {
      if (!value) return true;
      const isValid = phoneRegExp.test(value);
      return isValid || this.createError({ message: t('INVALID_PHONE_NUMBER') });
    }),
  address: Yup.string().nullable(),
});

export const loginEmailValidationSchema = () => Yup.object().shape({
  email: Yup.string()
    .required(t('PLEASE_COMPLETE_REQUIRED_FIELDS'))
    .email(t('INVALID_EMAIL')),
  password: Yup.string()
    .required(t('PLEASE_COMPLETE_REQUIRED_FIELDS'))
    .min(6, t('PASSWORD_MUST_BE_6_CHARS')),
});

export const loginSMSValidationSchema = () => Yup.object().shape({
  phone: Yup.string()
    .required(t('PLEASE_ENTER_MOBILE_NUMBER'))
    .test('phone-format', function(value) {
      if (!value) return true;
      const isValid = phoneRegExp.test(value);
      return isValid || this.createError({ message: t('INVALID_PHONE_NUMBER') });
    }),
});

export const changeAvatarValidationSchema = () => Yup.object().shape({
  avatar: Yup.string(),
  link: Yup.string()
    .url(t('INVALID_LINK'))
    .test("is-image-format", function(value) {
      if (!value) return true; // אם אין ערך, לא לבדוק
      const extension = value.split('.').pop()?.toLowerCase();
      const isValid = extension ? imageFormats.includes(extension) : false;
      return isValid || this.createError({ message: t('LINK_MUST_LEAD_TO_IMAGE') });
    }),
}).test("at-least-one", function(values) {
  if (!values) return false; // אם אין ערכים, להחזיר false
  const isValid = !!values.link || !!values.avatar; // לפחות אחד חייב להיות מלא
  return isValid || this.createError({ message: t('UPLOAD_IMAGE_OR_LINK') });
});

export const restorePasswordValidationSchema = () => Yup.object().shape({
  email_or_phone: Yup.string()
    .required(t('PLEASE_ENTER_EMAIL_OR_PHONE'))
    .test("email_or_phone", function(value) {
      if (!value) return true;
      let isValid;
      if (value.includes("@")) {
        isValid = validateEmail(value);
      } else {
        isValid = isValidPhone(value);
      }
      return isValid || this.createError({ message: t('INVALID_PHONE_OR_EMAIL') });
    }),
});

const validateEmail = (email: string) => {
  return Yup.string().email().isValidSync(email);
};

export const passwordValidationSchema = () => Yup.object().shape({
  password: Yup.string()
    .required(t('THE_PASSWORD_IS_REQUIRED'))
    .min(6, t('PASSWORD_MUST_BE_6_CHARS_1'))
    .test('has-letter', function(value) {
      if (!value) return true;
      const isValid = /[A-Za-zא-ת]/.test(value);
      return isValid || this.createError({ message: t('PASSWORD_MUST_INCLUDE_LETTER') });
    })
    .test('has-number', function(value) {
      if (!value) return true;
      const isValid = /\d/.test(value);
      return isValid || this.createError({ message: t('PASSWORD_MUST_INCLUDE_NUMBER') });
    })
    .test('has-special', function(value) {
      if (!value) return true;
      const isValid = /[!@#$%^&*(),.?":{}|<>]/.test(value);
      return isValid || this.createError({ message: t('PASSWORD_MUST_INCLUDE_SPECIAL') });
    }),

  repeatPassword: Yup.string()
    .oneOf([Yup.ref("password")], t('PASSWORDS_MUST_BE_IDENTICAL'))
    .required(t('CONFIRM_NEW_PASSWORD')),
});
